import { ref } from 'vue';
import { BASE_URL, DOWNLOAD_PARSED_PDF_API_ENDPOINT } from "@/assets/constants/api_endpoints";
import { FAILED_STATUS, LOAD_PDF_FAILED_ERR_MSG } from "@/assets/constants/constants.js";
import { createAuthenticatedAxiosInstance } from '@/services/api';

/**
 * Composable for handling parsed OCR results
 * Manages loading, normalizing, and grouping parsed lines
 */
export function useParsedResults() {
    const parsedLoading = ref(false);
    const parsedError = ref(false);
    const parsedData = ref(null);
    const parsedLines = ref([]);
    const pageGroupedLines = ref({});
    const availablePages = ref([]);

    const loadParsedResults = async (resourceId, isSample, onError) => {
        if (!resourceId) return;
        parsedLoading.value = true;
        parsedError.value = false;

        try {
            const axiosInstance = createAuthenticatedAxiosInstance();

            const url = `${BASE_URL}${DOWNLOAD_PARSED_PDF_API_ENDPOINT}?resource_id=${resourceId}&is_sample=${isSample}`;
            const response = await axiosInstance.get(url);

            if (response.status === 200 && response.data) {
                parsedData.value = response.data;
                const textLines = extractTextLines(response.data);
                parsedLines.value = normalizeParsedLines(textLines);
                groupLinesByPage();
            }
        } catch (error) {
            parsedError.value = true;
            if (onError) onError(error);
        } finally {
            parsedLoading.value = false;
        }
    };

    const extractTextLines = (data) => {
        if (!data) return [];
        
        // Handle different data formats
        if (Array.isArray(data)) {
            // Array of pages: [{ page: 1, text_lines: [...] }, { page: 2, text_lines: [...] }, ...]
            return data.flatMap((pageObj, idx) => {
                const pageNum = pageObj.page !== undefined ? pageObj.page : idx + 1; // 1-indexed by default
                const textLines = pageObj.text_lines || [];
                // Ensure each line has the page number
                return textLines.map(line => ({
                    ...line,
                    page: line.page !== undefined ? line.page : pageNum
                }));
            });
        }
        
        // If data is an object with resource keys
        const allLines = [];
        for (const key in data) {
            if (Array.isArray(data[key])) {
                allLines.push(...data[key].flatMap((pageObj, idx) => {
                    const pageNum = pageObj.page !== undefined ? pageObj.page : idx + 1;
                    const textLines = pageObj.text_lines || [];
                    return textLines.map(line => ({
                        ...line,
                        page: line.page !== undefined ? line.page : pageNum
                    }));
                }));
            }
        }
        return allLines;
    };

    const normalizeParsedLines = (textLines) => {
        const normalized = [];
        
        for (const tl of textLines) {
            const polygon = parsePolygon(tl.polygon);
            const text = tl.text || '';
            const page = tl.page !== undefined ? tl.page : 0;
            const imageBbox = tl.image_bbox || [0, 0, 560, 794];
            const confidence = tl.confidence || 0;

            if (polygon.length > 0) {
                normalized.push({ points: polygon, text, page, imageBbox, confidence });
            } else {
                normalized.push({ points: [], text, page, imageBbox, confidence });
            }
        }
        
        return normalized;
    };

    const parsePolygon = (poly) => {
        if (!poly) return [];
        
        if (typeof poly === 'string') {
            // Parse string format: "x1,y1;x2,y2;..." or "x1,y1 x2,y2 ..."
            const separator = poly.includes(';') ? ';' : ' ';
            const pts = poly.split(separator).map(p => {
                const [x, y] = p.split(',').map(Number);
                return { x, y };
            });
            return pts;
        }
        
        if (Array.isArray(poly)) {
            if (poly.length === 0) return [];
            
            // Check if first element is number (flat array)
            if (typeof poly[0] === 'number') {
                const pts = [];
                for (let i = 0; i < poly.length; i += 2) {
                    pts.push({ x: poly[i], y: poly[i + 1] });
                }
                return pts;
            }
            
            // If first element is array [[x,y],...]
            if (Array.isArray(poly[0])) {
                return poly.map(([x, y]) => ({ x, y }));
            }
            
            // If objects with x,y
            if (poly[0] && (poly[0].x !== undefined || poly[0].X !== undefined)) {
                return poly.map(p => ({ x: p.x || p.X, y: p.y || p.Y }));
            }
        }
        
        return [];
    };

    const groupLinesByPage = () => {
        pageGroupedLines.value = {};
        availablePages.value = [];
        
        for (const ln of parsedLines.value) {
            const page = ln.page !== undefined ? ln.page : 0;
            if (!pageGroupedLines.value[page]) {
                pageGroupedLines.value[page] = [];
                availablePages.value.push(page);
            }
            pageGroupedLines.value[page].push(ln);
        }
        
        availablePages.value.sort((a, b) => a - b);
    };

    return {
        parsedLoading,
        parsedError,
        parsedData,
        parsedLines,
        pageGroupedLines,
        availablePages,
        loadParsedResults,
        groupLinesByPage,
    };
}
